import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../api';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#E4572E] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="bg-[#F8FAFC] min-h-screen p-6">

      {/* Header */}
      <div className="mb-10">
        <h2 className="text-3xl font-bold text-[#0F2F3D]">Admin Dashboard</h2>
        <p className="text-gray-500 mt-1">Overview of your platform performance</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">

        {/* Card Component */}
        {[
          {
            title: "Total Users",
            value: stats?.totalUsers || 0,
            subtitle: "Registered members"
          },
          {
            title: "Total Products",
            value: stats?.totalProducts || 0,
            subtitle: "Available products"
          },
          {
            title: "Total Income",
            value: `₹${stats?.totalIncome?.toLocaleString() || 0}`,
            subtitle: "Platform earnings"
          },
          {
            title: "Total Purchases",
            value: stats?.totalPurchases || 0,
            subtitle: "Completed orders"
          }
        ].map((card, index) => (
          <div
            key={index}
            className="bg-white rounded-2xl shadow-md p-6 hover:shadow-xl transition-all duration-300 border-t-4 border-[#E4572E]"
          >
            <div className="text-sm font-semibold text-gray-600 mb-2">
              {card.title}
            </div>

            <div className="text-3xl font-bold text-[#0F2F3D]">
              {card.value}
            </div>

            <div className="text-xs text-gray-500 mt-2">
              {card.subtitle}
            </div>
          </div>
        ))}

      </div>

      {/* Payment Analytics Section */}
      <div className="mb-10">
        <h3 className="text-2xl font-bold text-[#0F2F3D] mb-6">Payment Analytics</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          {/* Total Payments */}
          <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-xl transition-all duration-300 border-t-4 border-blue-500">
            <div className="text-sm font-semibold text-gray-600 mb-2">Total Payments</div>
            <div className="text-3xl font-bold text-[#0F2F3D]">{stats?.paymentAnalysis?.totalCount || 0}</div>
            <div className="text-xs text-gray-500 mt-2">Total submitted</div>
          </div>
          
          {/* Total Wallet Top-ups */}
          <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-xl transition-all duration-300 border-t-4 border-purple-500">
            <div className="text-sm font-semibold text-gray-600 mb-2">Total Transferred</div>
            <div className="text-3xl font-bold text-[#0F2F3D]">
              ₹{stats?.paymentAnalysis?.totalWalletTopupAmount?.toLocaleString() || 0}
            </div>
            <div className="text-xs text-gray-500 mt-2">
              {stats?.paymentAnalysis?.totalWalletTopupCount || 0} transactions
            </div>
          </div>
          
          {/* Approved Payments */}
          <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-xl transition-all duration-300 border-t-4 border-green-500">
            <div className="text-sm font-semibold text-gray-600 mb-2">Approved</div>
            <div className="text-3xl font-bold text-green-600">{stats?.paymentAnalysis?.VERIFIED?.count || 0}</div>
            <div className="text-xs text-gray-500 mt-2">
              ₹{stats?.paymentAnalysis?.VERIFIED?.amount?.toLocaleString() || 0}
            </div>
          </div>
          
          {/* Pending Payments */}
          <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-xl transition-all duration-300 border-t-4 border-yellow-500">
            <div className="text-sm font-semibold text-gray-600 mb-2">Pending</div>
            <div className="text-3xl font-bold text-yellow-600">{stats?.paymentAnalysis?.SUBMITTED?.count || 0}</div>
            <div className="text-xs text-gray-500 mt-2">
              ₹{stats?.paymentAnalysis?.SUBMITTED?.amount?.toLocaleString() || 0}
            </div>
          </div>
          
          {/* Rejected Payments */}
          <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-xl transition-all duration-300 border-t-4 border-red-500">
            <div className="text-sm font-semibold text-gray-600 mb-2">Rejected</div>
            <div className="text-3xl font-bold text-red-600">{stats?.paymentAnalysis?.REJECTED?.count || 0}</div>
            <div className="text-xs text-gray-500 mt-2">
              ₹{stats?.paymentAnalysis?.REJECTED?.amount?.toLocaleString() || 0}
            </div>
          </div>
        </div>
      </div>

      {/* Recent + Top Earners Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

        {/* Recent Users */}
        <div className="bg-white rounded-2xl shadow-md p-6">
          <h3 className="text-xl font-bold text-[#0F2F3D] mb-5 border-b pb-3">
            Recent Users
          </h3>

          <div className="space-y-3">
            {stats?.recentUsers?.slice(0, 5).map((user, index) => (
              <div
                key={index}
                className="flex justify-between items-center p-4 bg-[#F8FAFC] rounded-xl hover:bg-[#E4572E]/5 transition-all"
              >
                <div>
                  <p className="font-semibold text-[#0F2F3D]">{user.name}</p>
                  <p className="text-sm text-gray-500">{user.email}</p>
                </div>

                <div className="text-right">
                  <span className="inline-block px-3 py-1 text-xs font-semibold bg-[#E4572E]/10 text-[#E4572E] rounded-full">
                    {user.rank}
                  </span>
                  <p className="text-xs text-gray-400 mt-1">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Earners */}
        <div className="bg-white rounded-2xl shadow-md p-6">
          <h3 className="text-xl font-bold text-[#0F2F3D] mb-5 border-b pb-3">
            Top Earners
          </h3>

          <div className="space-y-3">
            {stats?.topEarners?.slice(0, 5).map((user, index) => (
              <div
                key={index}
                className="flex justify-between items-center p-4 bg-[#F8FAFC] rounded-xl hover:bg-[#E4572E]/5 transition-all"
              >
                <div>
                  <p className="font-semibold text-[#0F2F3D]">{user.name}</p>
                  <p className="text-sm text-gray-500">{user.rank}</p>
                </div>

                <div className="text-right">
                  <p className="text-sm font-bold text-[#E4572E]">
                    ₹{user.wallet?.mainWallet?.toLocaleString() || 0}
                  </p>
                  <p className="text-xs text-gray-400">
                    Total earned
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>

    </div>
  );
};

export default AdminDashboard;